import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';

export interface UploadResult {
  ok: boolean;
  savedName: string;
}

export interface ProfileTokenUsage {
  total: {
    total_calls: number;
    total_input_tokens: number;
    total_output_tokens: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
  };
}

export interface Profile {
  dataset_name: string;
  dataset_type: string;
  n_rows?: number;
  n_columns?: number;
  token_usage?: ProfileTokenUsage;
}

export interface TransformStep {
  op: string;
  input?: string;
  output?: string;
  prefix?: string;
  suffix?: string;
  params?: Record<string, any>;
  [key: string]: any;
}

export interface ExecutionPreviewData {
  raw: CsvPreviewData;
  harmonized: CsvPreviewData;
  total: number;
  page: number;
  page_size: number;
}

export interface AuditVariable {
  id: string;
  decision: string;
  mapping_from: string | string[];
  mapping_to: string;
  decision_confidence?: number;
  target_confidence?: number;
  transform_confidence?: number;
  reasoning?: string;
  transform: { steps: TransformStep[] };
  metadata?: { review_reasons?: string[] };
  reviewed?: boolean;
}

export interface TargetValueEntry { value: string; label: string; }
export interface ExampleMapping { from: string; to: string; }

export interface TargetHelp {
  description: string;
  what_it_means: string;
  values: TargetValueEntry[];
  relevant_info: string[];
  example_mappings: ExampleMapping[];
}

export interface TargetAlternative {
  id: string;
  label: string;
  confidence: number;
  system_selected: boolean;
  help: TargetHelp;
}

export interface TransformPreviewRow { original: string; new_value: string; }

export interface TransformationOption {
  rank: number;
  title: string;
  description?: string;
  confidence: number;
  system_selected: boolean;
  ai_suggested?: boolean;
  notes?: string;
  preview: TransformPreviewRow[];
  code: Record<string, unknown> | unknown[];
}

export interface TransformChatMessage {
  role: 'user' | 'assistant';
  content: string;
  transformation?: TransformationOption;
  error?: boolean;
  initial?: boolean;
  isHtml?: boolean;
}

export interface TransformSuggestion {
  message: string;
  transformation?: Partial<TransformationOption>;
}

export interface ReviewVariableData {
  source_info: {
    id: string;
    decision: string;
    decision_system_selected: string | null;
    mapping_from: string | string[];
    mapping_to: string;
    label?: string;
    type: string;
    unique_count: number;
    null_pct: number;
    decision_confidence: number;
    decision_confidences: Record<string, number>;
    target_confidence: number | null;
    transform_confidence: number | null;
    selected_transform_rank?: number | null;
  };
  target_alternatives: TargetAlternative[];
  transformation_options: TransformationOption[];
}

export interface TargetReloadData {
  target_alternatives: TargetAlternative[];
  transformation_options: TransformationOption[];
}

export interface TransformReloadData {
  transformation_options: TransformationOption[];
}

export interface PlanJson {
  plan_name: string;
  variables: AuditVariable[];
}

export interface HumanReviewQueue {
  ids: string[];
  total: number;
}

export interface ImageFolderList {
  directories: string[];
}

export interface ImageSummaryData {
  ok: boolean;
  counts: {
    identified: number;
    skull_stripping: number;
    reorientation: number;
    registration: number;
  };
}

export interface ImageGifList {
  ok: boolean;
  gifs: string[];
  total: number;
  page: number;
  page_size: number;
}

export interface PagedResult<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

export type ProfileVariableRow = {
  variable_name: string;
  data_type: string;
  description: string;
  missing_pct?: number | null;
  confidence_data_type?: number | null;
  confidence_description?: number | null;
  confidence_values?: number | null;
  confidence_overall?: number | null;
  values?: any;
};

export interface StreamStatus {
  message: string;
  status?: string;
}

export interface UploadFilePreview {
  type: 'csv' | 'pdf';
  headers?: string[];
  rows?: string[][];
  total_columns?: number;
  total_rows?: number;
}

export interface UploadFileInfo {
  name: string;
  size: number;
  preview?: UploadFilePreview;
}

export interface CsvPreviewData {
  columns: string[];
  rows: Record<string, string>[];
}

export interface ValidationRow {
  variable: string;
  decision_id: string | null;
  mapping_from: string[];
  harmonized: boolean | null;
  concept: string;
  datatype: string;
  coverage: string;
  missing: string;
  stats: string;
}

export interface SplitVariableResult {
  created_variable_ids: string[];
}

export interface PipelineStats {
  dataset_name: string;
  modules: { label: string; minutes: number }[];
}

export interface PlanQueryOpts {
  /** Active filter values sent as comma-separated `filter=` param.
   *  Possible values: matched | mapped | partially_mapped | calculated_variable | not_mapped
   *                   intervention_needed | intervention_not_needed */
  filters?: string[];
  /** When true, sends replan_filter=true — restricts to replan-failed variables */
  replanFilter?: boolean;
  /** Confidence threshold (0–1); required when filters includes intervention_needed or intervention_not_needed */
  threshold?: number;
  /** Server-side sort (confidence_desc | confidence_asc | reviewed_first | reviewed_last) */
  sortBy?: string;
}

export interface ValidationSummary {
  total: number;
  harmonized: number;
  not_harmonized: number;
  not_mapped: number;
  harmonization_rate: number;
  dataset_name: string;
}

export interface SessionFiles {
  unharmonized_dataset: UploadFileInfo;
  standard_dataset: UploadFileInfo;
  unharmonized_codebook: UploadFileInfo;
  standard_codebook: UploadFileInfo;
}

export interface Session {
  session_id: string;
  name: string;
  status: 'running' | 'finished' | 'failed';
  current_step: string;
  created_at: string;
  updated_at: string;
  filter_active?: boolean;
  files?: SessionFiles;
}

@Injectable()
export abstract class AppService {
  // Stored by the service after a successful Start Profiling / Start Preprocessing.
  // Components never touch this — all real-service methods read it internally.
  sessionId = '';
  sessionName = signal('');
  pendingSessionFiles = signal<SessionFiles | null>(null);

  setSession(id: string): void { this.sessionId = id; }
  abstract setRunName(name: string): void;

  abstract uploadFile(file: File, card: string, slot: number): Promise<UploadResult>;
  abstract saveSteps(decisionId: string, steps: unknown[]): void;
  abstract getHumanReviewQueue(threshold: number | null): Observable<HumanReviewQueue>;
  abstract checkAllNotMapped(): Observable<{ all_not_mapped: boolean }>;
  abstract getProfiles(): Observable<Record<string, Profile>>;
  abstract listUploadFiles(): Observable<UploadFileInfo[]>;
  abstract getUploadFilePreviewUrl(name: string): string;
  abstract getPreviewLoadDelay(): number;
  getPdfPreviewShouldFail(): boolean { return false; }
  abstract watchUploadStatus(): Observable<StreamStatus>;
  abstract listImageFolders(path: string): Observable<ImageFolderList>;
  abstract getImageSummary(folder: string): Observable<ImageSummaryData>;
  abstract listImageGifs(folder: string, page: number, pageSize: number): Observable<ImageGifList>;
  abstract runPreprocessing(folder: string): Observable<void>;
  abstract watchPreprocessingStatus(folder: string): Observable<StreamStatus>;
  abstract getGifUrl(path: string): string;

  abstract getValidationSummary(): Observable<ValidationSummary>;
  abstract notifyReplan(failedVariables: string[]): Observable<void>;
  abstract getPipelineStats(): Observable<PipelineStats>;

  // ── Strategy & Planning ───────────────────────────────────

  abstract getReviewData(decisionId: string): Observable<ReviewVariableData>;

  // Step 1 → Step 2: user changed decision
  abstract startDecisionChange(decisionId: string, decision: string): Observable<{ ok: boolean }>;
  abstract watchDecisionChangeStatus(decisionId: string, decision: string): Observable<StreamStatus>;
  abstract getDecisionTargets(decisionId: string, decision: string): Observable<TargetReloadData>;

  // Step 2 → Step 3: user changed target
  abstract startTransformReload(decisionId: string, decision: string, targetId: string): Observable<{ ok: boolean }>;
  abstract watchTransformReloadStatus(decisionId: string, decision: string, targetId: string): Observable<StreamStatus>;
  abstract getTargetTransforms(decisionId: string, targetId: string): Observable<TransformReloadData>;

  abstract markVariableReviewed(
    decisionId: string,
    decision: string,
    targetId: string | null,
    selectedTransformation: TransformationOption | null,
  ): Observable<void>;

  abstract splitVariable(decisionId: string): Observable<SplitVariableResult>;

  abstract suggestTransformation(
    decisionId: string,
    decision: string,
    targetId: string,
    selectedTransformation: TransformationOption | null,
    userMessage: string,
    history: TransformChatMessage[],
  ): Observable<TransformSuggestion>;

  // ── Pipeline status streams ───────────────────────────────

  abstract reconnectUploadStatus(): Observable<StreamStatus>;
  abstract watchProfilingStatus(): Observable<StreamStatus>;
  abstract reconnectProfilingStatus(): Observable<StreamStatus>;
  abstract watchPlanningStatus(): Observable<StreamStatus>;
  abstract reconnectPlanningStatus(): Observable<StreamStatus>;
  abstract watchExecutionStatus(): Observable<StreamStatus>;
  abstract reconnectExecutionStatus(): Observable<StreamStatus>;
  abstract startExecution(): Observable<{ ok: boolean; session_id: string }>;
  abstract getExecutionPreview(page: number, pageSize: number, colLimit?: number): Observable<ExecutionPreviewData>;
  abstract getPlanVariables(page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>>;
  abstract searchPlanVariables(q: string, page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>>;
  abstract getValidationResults(page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>>;
  abstract searchValidationResults(q: string, page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>>;
  abstract searchProfileVariables(dataset: string, q: string, page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ProfileVariableRow>>;
  abstract getProfileVariables(dataset: string, page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ProfileVariableRow>>;

  // ── Session history ───────────────────────────────────────

  abstract getSessions(): Observable<Session[]>;
  abstract renameSession(sessionId: string, name: string): Observable<{ ok: boolean }>;
  abstract deleteSession(sessionId: string): Observable<void>;
  abstract finalizeSession(): Observable<{ ok: boolean }>;
}

@Injectable({ providedIn: 'root' })
export class HelpBubbleService {
  readonly hasContent = signal(false);
  readonly isOpen = signal(false);

  register(): void { this.hasContent.set(true); }
  unregister(): void { this.hasContent.set(false); this.isOpen.set(false); }
  toggle(): void { this.isOpen.update(v => !v); }
  close(): void { this.isOpen.set(false); }
}
